import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import {
  faBell,
  faCircleInfo,
  faCircleXmark,
  faEnvelopeCircleCheck,
  faFileCircleCheck,
  faFileCircleXmark,
  faFileInvoice,
  faHourglassHalf,
  faPaperclip,
  faPenToSquare,
  faPeopleArrows,
  faRightLeft,
  faRotateLeft,
  faTrashCan,
  faTriangleExclamation,
  faUserXmark,
} from '@fortawesome/free-solid-svg-icons';
import { leerBooleano } from '../../utils/leerCampo';
import { normalizarDatos } from './datosAvisos';

/** Tono de una categoría del buzón: decide los colores de su ícono en el panel. */
export type TonoCategoriaBuzon = 'host' | 'warning' | 'error' | 'info';

/** Cómo se ve una categoría del buzón en el panel de la campanita. */
export interface CategoriaVisualBuzon {
  icono: IconDefinition;
  tono: TonoCategoriaBuzon;
}

/**
 * Clases de Tailwind por tono, con los tokens de `styles.css` (fondo `-50`, ícono `-600`/`-700`).
 * Van completas para que Tailwind las encuentre al escanear el código.
 */
export const CLASES_TONO_BUZON: Record<TonoCategoriaBuzon, { fondo: string; icono: string }> = {
  host: { fondo: 'bg-host-50', icono: 'text-host-600' },
  warning: { fondo: 'bg-warning-50', icono: 'text-warning-700' },
  error: { fondo: 'bg-error-50', icono: 'text-error-600' },
  info: { fondo: 'bg-info-50', icono: 'text-info-600' },
};

/** Para una categoría que el front todavía no conoce: el catálogo vive en el SSO. */
const CATEGORIA_POR_DEFECTO: CategoriaVisualBuzon = { icono: faBell, tono: 'host' };

const CATEGORIAS: Partial<Record<string, CategoriaVisualBuzon>> = {
  ASIGNACIONES: { icono: faFileInvoice, tono: 'host' },
  CAMBIOS_FACTURA: { icono: faPenToSquare, tono: 'host' },
  PENDIENTES_CONFIRMAR: { icono: faHourglassHalf, tono: 'warning' },
  DEVUELTOS: { icono: faRotateLeft, tono: 'warning' },
  RECHAZADOS: { icono: faCircleXmark, tono: 'error' },
  AVISOS_CANCELACION_CFDI: { icono: faFileCircleXmark, tono: 'error' },
  SOLICITUDES_BORRADO_CFDI: { icono: faTrashCan, tono: 'error' },
  SOLICITUDES_REASIGNACION: { icono: faPeopleArrows, tono: 'info' },
  AVISOS_SISTEMA: { icono: faCircleInfo, tono: 'info' },
};

/**
 * Claves de tipo de evento de los avisos de Materialidad (conciliación SAT y reasignación). Todos viven
 * en la categoría `ASIGNACIONES`, así que lo que los distingue es `ClaveTipoEvento`.
 * Contrato: `docs/BUZON.md` del repo `RfacilBack_SM_BC`.
 */
export const TIPO_EVENTO_BUZON = {
  FacturaAsignada: 'FACTURA_ASIGNADA_PORTAL',
  FacturaReasignada: 'FACTURA_REASIGNADA_PORTAL',
  SolicitudReasignacion: 'SOLICITUD_REASIGNACION',
  SolicitudReasignacionRechazada: 'SOLICITUD_REASIGNACION_RECHAZADA',
  MaterialidadRelacionada: 'FACTURA_MATERIALIDAD_RELACIONADA',
  FacturaCanceladaSat: 'FACTURA_CANCELADA_SAT',
  FacturaNoReconocidaCancelada: 'FACTURA_NO_RECONOCIDA_CANCELADA',
  /** Informativo: se pidió (o se intentó pedir) por correo al proveedor que cancele una factura marcada no reconocida. */
  FacturaNoReconocidaProveedorNotificado: 'FACTURA_NO_RECONOCIDA_PROVEEDOR_NOTIFICADO',
  /** Accionable para el administrador: a un usuario eliminado le quedaron facturas por reasignar (uno por usuario, SSO `BZ016`). */
  FacturasPorReasignarBaja: 'FACTURAS_POR_REASIGNAR_BAJA',
} as const;

/**
 * Ícono y tono por tipo de evento. Un tipo que no está aquí cae a su categoría.
 * TODO(marbella): íconos y tonos según el diseño de PES-397, PES-411, PES-412 y PES-759.
 */
const TIPOS_EVENTO: Partial<Record<string, CategoriaVisualBuzon>> = {
  [TIPO_EVENTO_BUZON.FacturaReasignada]: { icono: faRightLeft, tono: 'info' },
  [TIPO_EVENTO_BUZON.SolicitudReasignacion]: { icono: faPeopleArrows, tono: 'warning' },
  [TIPO_EVENTO_BUZON.SolicitudReasignacionRechazada]: { icono: faCircleXmark, tono: 'error' },
  [TIPO_EVENTO_BUZON.FacturasPorReasignarBaja]: { icono: faUserXmark, tono: 'error' },
  [TIPO_EVENTO_BUZON.MaterialidadRelacionada]: { icono: faPaperclip, tono: 'info' },
  [TIPO_EVENTO_BUZON.FacturaCanceladaSat]: { icono: faFileCircleXmark, tono: 'error' },
  // Informativo: el SAT ya canceló la factura que había que pedirle al proveedor; no hay nada que hacer.
  [TIPO_EVENTO_BUZON.FacturaNoReconocidaCancelada]: { icono: faFileCircleCheck, tono: 'info' },
  // Informativo: el correo al proveedor salió. Si no salió (`Datos.Enviado === false`) cambia a advertencia, ver abajo.
  [TIPO_EVENTO_BUZON.FacturaNoReconocidaProveedorNotificado]: { icono: faEnvelopeCircleCheck, tono: 'info' },
};

/** El correo al proveedor no se pudo enviar: el responsable debe contactarlo por otro medio. */
const VISUAL_PROVEEDOR_NO_NOTIFICADO: CategoriaVisualBuzon = { icono: faTriangleExclamation, tono: 'warning' };

/**
 * @function normalizarClaveCategoria
 * @description `ClaveCategoria` sin espacios a los lados y en mayúsculas, como las claves del
 * catálogo del SSO (`DEVUELTOS`). Vacía si no vino.
 */
export const normalizarClaveCategoria = (clave: string | null | undefined): string =>
  (clave ?? '').trim().toUpperCase();

/**
 * @function categoriaVisualBuzon
 * @description Ícono y tono de una categoría por su `ClaveCategoria`, sin importar mayúsculas ni
 * espacios. Una clave que no está en el mapa (o que no vino) usa la campana con el tono host. El
 * nombre visible de la categoría sigue viniendo del SSO (`CategoriaBuzonDTO.Nombre`).
 */
export const categoriaVisualBuzon = (clave: string | null | undefined): CategoriaVisualBuzon =>
  CATEGORIAS[normalizarClaveCategoria(clave)] ?? CATEGORIA_POR_DEFECTO;

/**
 * @function normalizarClaveTipoEvento
 * @description `ClaveTipoEvento` sin espacios a los lados y en mayúsculas, como las claves del SSO
 * (`FACTURA_CANCELADA_SAT`). Vacía si no vino.
 */
export const normalizarClaveTipoEvento = (clave: string | null | undefined): string => (clave ?? '').trim().toUpperCase();

/**
 * @function visualNotificacionBuzon
 * @description Ícono y tono de una notificación: el de su tipo de evento si el front lo conoce (avisos de
 * conciliación y reasignación) y, si no, el de su categoría. Así un tipo nuevo del SSO nunca rompe la tarjeta.
 */
export const visualNotificacionBuzon = (n: {
  ClaveTipoEvento?: string | null;
  ClaveCategoria?: string | null;
  Datos?: unknown;
}): CategoriaVisualBuzon => {
  const tipo = normalizarClaveTipoEvento(n.ClaveTipoEvento);
  // Solo el tono mira `Datos`, y solo `Enviado === false` (el texto lo trae el servidor): sin dato legible no se
  // inventa un fallo.
  if (tipo === TIPO_EVENTO_BUZON.FacturaNoReconocidaProveedorNotificado && leerBooleano(normalizarDatos(n.Datos), 'Enviado') === false) {
    return VISUAL_PROVEEDOR_NO_NOTIFICADO;
  }
  return TIPOS_EVENTO[tipo] ?? categoriaVisualBuzon(n.ClaveCategoria);
};
